import { Directionality } from '@angular/cdk/bidi';
import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterLink, RouterOutlet } from '@angular/router';
import { DataTransferIndicatorComponent } from '@app/data-transfer/data-transfer-indicator/data-transfer-indicator.component';
import { NotificationNegotiationComponent } from '@app/notification/notification-negotiation.component';
import { BreadcrumbService } from '@app/shared/breadcrumb/breadcrumb.service';
import { ContextMenuComponent } from '@app/shared/context-menu/context-menu.component';
import { DynamicCssLoaderComponent } from '@app/shared/dynamic-css-loader/dynamic-css-loader.component';
import { LogService } from '@app/shared/logging/log.service';
import { Constants } from '@app/shared/models/constants';
import { StripHtmlPipe } from '@app/shared/pipes/strip-html.pipe';
import { PrimeNgTranslationsService } from '@app/shared/primeng/primeng-translations.service';
import { AuthService } from '@app/shared/services/auth.service';
import { DeviceService } from '@app/shared/services/device.service';
import { FeaturebaseService } from '@app/shared/featurebase/featurebase.service';
import { ThemeService } from '@app/shared/services/theme.service';
import { LoadUserLanguageComponent } from '@app/shared/translate/load-user-language.component';
import { PrimeNG } from 'primeng/config';
import { Ripple } from 'primeng/ripple';
import { Toast, ToastPositionType } from 'primeng/toast';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	imports: [
		LoadUserLanguageComponent,
		DynamicCssLoaderComponent,
		RouterOutlet,
		NotificationNegotiationComponent,
		Toast,
		NgClass,
		Ripple,
		RouterLink,
		ContextMenuComponent,
		DataTransferIndicatorComponent,
		StripHtmlPipe
	]
})
export class AppComponent implements OnInit {

	toastKey = Constants.AppToastKey;

	get toastPosition (): ToastPositionType {
		return this.dir.value === 'ltr' ? 'top-right' : 'top-left';
	}

	constructor (
		private readonly deviceService: DeviceService,
		private readonly themeService: ThemeService,
		private readonly logService: LogService,
		private readonly primeNG: PrimeNG,
		private readonly primeNgTranslationsService: PrimeNgTranslationsService,
		private readonly authService: AuthService,
		private readonly breadcrumbService: BreadcrumbService,
		private readonly featureBaseService: FeaturebaseService,
		private readonly dir: Directionality
	) { }

	ngOnInit (): void {

		/**
		 * Let's not put anything here that requires authentication as errors occur in certain conditions, particularly when using SSO.
		 */

		this.logService.information({ message: 'Application initialization' });
		this.deviceService.initialize();
		this.themeService.initialize();
		this.breadcrumbService.initialize();

		this.configurePrimeNg();
		this.clearAuthStorage();

		this.authService.onAuthenticationSucceed.subscribe(() => {
			this.featureBaseService.initialize();
		});
	}

	private configurePrimeNg (): void {
		this.primeNG.setTranslation(this.primeNgTranslationsService.getTranslations());
	}

	/**
	 * If auth data exists in storage, either clear it if no longer valid or setup usual refresh
	 */
	private clearAuthStorage (): void {
		if (this.authService.getAccessToken()) {
			if (!this.authService.hasValidAccessToken()) {
				this.authService.clearAuthStorage();
			} else {
				this.authService.setupConditionalTokenRefresh();
				this.featureBaseService.initialize();
			}
		}
	}
}